import cx from 'classnames';
import { ChevronDown } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import 'react-calendar/dist/Calendar.css';
import DatePicker, { IDatePickerProps } from '../../../date-picker/date-picker';
import { isDate } from '../../../date-picker/model/types/IDateRange';
import style from '../../FilterPanel.module.scss';
import s from './DateRangeField.module.scss';
import { AppRangePicker } from '@fsd/shared/ui/AppDatePicker';
import dayjs from 'dayjs';

type DateRangeFieldProps = {
  value: [string, string];
  onChange: (val: [string, string]) => void;
  label?: string;
  variant?: IDatePickerProps['variant'];
  className?: string;
  disabled?: boolean;
  tabIndex?: number;
};

const format = (date: string) =>
  date ? new Date(date).toLocaleDateString('ru-RU') : '';

const todayDate = new Date();
const yesterdayDate = new Date(todayDate);
yesterdayDate.setDate(todayDate.getDate() - 1);
const weekAgoDate = new Date(todayDate);
weekAgoDate.setDate(todayDate.getDate() - 6);
const monthAgoDate = new Date(todayDate);
monthAgoDate.setMonth(todayDate.getMonth() - 1);
const formatRu = (date: Date) =>
  date.toLocaleDateString('ru-RU', { day: 'numeric', month: 'long' });

const DateRangeField = ({
  value,
  onChange,
  label,
  disabled,
  className,
  tabIndex,
  variant = 'mini',
}: DateRangeFieldProps) => {
  const [open, setOpen] = useState(false);
  const [showPicker, setShowPicker] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const [start, end] = value || ['', ''];

  const formatDateToISOString = (date: Date): string => {
    if (!date) return '';
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const handlePreset = (type: 'today' | 'yesterday' | 'week' | 'month') => {
    const today = new Date();
    if (type === 'today') {
      onChange([formatDateToISOString(today), formatDateToISOString(today)]);
    } else if (type === 'yesterday') {
      const yest = new Date(today);
      yest.setDate(today.getDate() - 1);
      onChange([formatDateToISOString(yest), formatDateToISOString(yest)]);
    } else if (type === 'week') {
      const weekAgo = new Date(today);
      weekAgo.setDate(today.getDate() - 6);
      onChange([formatDateToISOString(weekAgo), formatDateToISOString(today)]);
    } else if (type === 'month') {
      const monthAgo = new Date(today);
      monthAgo.setMonth(today.getMonth() - 1);
      onChange([formatDateToISOString(monthAgo), formatDateToISOString(today)]);
    }
    setOpen(false);
    setShowPicker(false);
  };

  useEffect(() => {
    if (!open) return;
    const handleClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
        setShowPicker(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  const periodOptions = [
    {
      label: 'За сегодня',
      onClick: () => handlePreset('today'),
      date: formatRu(todayDate),
    },
    {
      label: 'За вчера',
      onClick: () => handlePreset('yesterday'),
      date: formatRu(yesterdayDate),
    },
    {
      label: 'За неделю',
      onClick: () => handlePreset('week'),
      date: `${formatRu(weekAgoDate)} – ${formatRu(todayDate)}`,
    },
    {
      label: 'За месяц',
      onClick: () => handlePreset('month'),
      date: `${formatRu(monthAgoDate)} – ${formatRu(todayDate)}`,
    },
  ];

  return (
    <div
      className={cx(style.filterItem, className)}
      ref={ref}
      tabIndex={tabIndex}
    >
      {label && <label>{label}</label>}
      <div
        className={cx(s.dateInput, {
          [s.focused]: isFocused,
          [s.disabled]: disabled,
        })}
        onClick={() => !disabled && setOpen((prev) => !prev)}
        tabIndex={0}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
      >
        {start && end ? (
          <span className={s.headerText}>
            {`${format(start)} - ${format(end)}`}
          </span>
        ) : (
          <span className={s.placeholder}>Выбрать</span>
        )}
        <span className={cx(s.chevron, { [s.open]: open })}>
          <ChevronDown width={20} height={20} stroke="#8496AF" />
        </span>
      </div>
      {open && !disabled && !showPicker && (
        <div className={s.dropdown}>
          <div
            className={cx(s.menuItem, s.selectPeriod)}
            onClick={() => setShowPicker(true)}
          >
            Выбрать произвольный период
            <div className={s.menuItemDate}>Свои даты</div>
          </div>
          {periodOptions.map((opt) => (
            <div key={opt.label} className={s.menuItem} onClick={opt.onClick}>
              {opt.label}
              <div className={s.menuItemDate}>{opt.date}</div>
            </div>
          ))}
        </div>
      )}
      <div className={s.pickerAnchor}>
        <AppRangePicker
          open={open && showPicker}
          getPopupContainer={(trigger) =>
            ref.current ?? trigger.parentElement ?? document.body
          }
          value={[
            start ? dayjs(start) : null,
            end ? dayjs(end) : null,
          ]}
          onChange={(dates) => {
            if (!dates?.[0] || !dates?.[1]) return;

            onChange([
              dates[0].format('YYYY-MM-DD'),
              dates[1].format('YYYY-MM-DD'),
            ]);

            setOpen(false);
            setShowPicker(false);
          }}
          onOpenChange={(nextOpen) => {
            if (!nextOpen) {
              setOpen(false);
              setShowPicker(false);
            }
          }}
        />
      </div>
    </div>
  );
};

export default DateRangeField;
